/**
 * Faixa do último ciclo com o Protheus, acima dos filtros de toda grade
 * integrada: Pessoas, Frota, Conjuntos e Subcontratos seguem o mesmo desenho.
 *
 * A sincronização é automática, de hora em hora, e o botão de sincronizar agora
 * não tem relatório próprio: sem esta linha não haveria como saber, pela tela,
 * quando o último ciclo rodou e quanto ele mexeu. Só contagens; o detalhe por
 * registro fica no relatório da CLI, onde não há risco de espalhar dado pessoal.
 *
 * Rótulo em cima, valor embaixo, sem parêntese nem travessão encaixando
 * informação.
 */
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { http } from '../../core/api/client'
import { CATALOG_STALE_TIME } from '../../core/query/queryClient'
import { StatusChip } from '../components/StatusChip'
import { fmtNum } from '../format'
import { useI18n } from '@/core/i18n/useI18n'
import type { ListParams } from '../../core/api/resource'
import { ResumoSituacaoBanner } from '../components/ResumoSituacaoBanner'

export interface ContagemBanner {
  rotulo: string
  /** Chave numérica no payload. */
  chave: string
}

interface Resumo {
  synced_at: string | null
  [chave: string]: number | string | null
}

export function ProtheusBanner({ url, queryKey, contagens }: {
  url: string
  /** A queryKey começa pelo nome do recurso da grade, para o botão de
   *  sincronizar invalidar a faixa junto com a lista. */
  queryKey: readonly string[]
  contagens: ContagemBanner[]
}) {
  const { t } = useI18n()
  const q = useQuery({
    queryKey,
    queryFn: ({ signal }) => http.get<Resumo | null>(url, { signal }),
    staleTime: CATALOG_STALE_TIME,
  })
  const r = q.data
  if (!r?.synced_at) return null
  // Contagem ausente no payload vira traço, e não zero: um ciclo antigo que não
  // gravou o número não sabe que ele é zero, e o zero seria uma resposta que a
  // faixa não tem.
  const itens = [
    { rotulo: t('protheus.banner.lastSync'), valor: new Date(r.synced_at).toLocaleString('pt-BR') },
    ...contagens.map((c) => ({
      rotulo: c.rotulo,
      valor: r[c.chave] == null ? '—' : fmtNum(Number(r[c.chave])),
    })),
  ]
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <StatusChip label="Protheus" tone="info" />
      {itens.map((i) => (
        <Box key={i.rotulo}>
          <Typography variant="caption" color="text.secondary" display="block">{i.rotulo}</Typography>
          <Typography variant="body2" fontWeight={600}>{i.valor}</Typography>
        </Box>
      ))}
    </Box>
  )
}

/** Último ciclo de veículos. Serve a Frota e aos dois Conjuntos, que nascem dele. */
export function FrotaProtheusBanner() {
  const { t } = useI18n()
  return (
    <ProtheusBanner
      url="/fleet/protheus/ultima-sincronizacao"
      queryKey={['fleet', 'protheus-sync']}
      contagens={[
        { rotulo: t('protheus.banner.vehicles'), chave: 'veiculos' },
        { rotulo: t('protheus.banner.created'), chave: 'criados' },
        { rotulo: t('protheus.banner.updated'), chave: 'atualizados' },
        { rotulo: t('protheus.banner.unchanged'), chave: 'inalterados' },
        // O que a origem mandou e não entrou no ciclo. Estava invisível: a faixa
        // contava só os já filtrados, e o descarte por duplicidade não deixava rastro
        // em lugar nenhum da interface.
        { rotulo: t('protheus.banner.outOfCycle'), chave: 'fora_do_ciclo' },
      ]}
    />
  )
}

/** Último ciclo de pessoas, dos dois perfis.
 *
 *  Duas leituras na mesma linha, e elas respondem a perguntas diferentes de
 *  propósito: à esquerda o ciclo do ERP, que fala da carga inteira e não se mexe;
 *  à direita a situação de quem está na tela AGORA, que acompanha filtro e busca
 *  (Samuel, 17/09/2026). O separador entre as duas é espaço e uma borda leve, não
 *  travessão.
 *
 *  `params` chega da grade. Sem ele a faixa da direita simplesmente conta o
 *  cadastro inteiro, que é o estado inicial da tela. */
export function PessoasProtheusBanner({ params }: { params?: ListParams } = {}) {
  const { t } = useI18n()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <ProtheusBanner
        url="/personnel/protheus/ultima-sincronizacao"
        queryKey={['personnel', 'protheus-sync']}
        contagens={[
          { rotulo: t('protheus.banner.drivers'), chave: 'motoristas' },
          { rotulo: t('protheus.banner.created'), chave: 'criados' },
          { rotulo: t('protheus.banner.updated'), chave: 'atualizados' },
          { rotulo: t('protheus.banner.unchanged'), chave: 'inalterados' },
        ]}
      />
      <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 3 }}>
        <ResumoSituacaoBanner path="/personnel" name="personnel" params={params} />
      </Box>
    </Box>
  )
}
